import Image from "next/image";

import cn from "classnames";

import { Price } from "@/components/price/price.component";
import { PromoBadge } from "@/components/promoBadge/promoBadge.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import {
  Chip,
  RecommendedMonitoringPlansQueryResult,
  RichText as RichTextType,
} from "@/sanity.types";

export const MonitoringPlanCard = ({
  badges,
  promotions,
  monitoringLegal,
  title,
  description,
  price,
  imageSrc,
  imageAlt,
  isSelected,
  onClick,
}: MonitoringPlanCardProps) => {
  const handleSelection = () => {
    if (!onClick) return;
    return (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    };
  };
  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleSelection}
      className={cn(
        "h-auto rounded-lg transition-colors",
        { "cursor-pointer border-2 hover:opacity-95": onClick },
        {
          "bg-primary-100 border-secondary ring-secondary ring-2 ring-offset-0":
            isSelected,
          "border-greyscalse": !isSelected,
        },
        { "border-greyscale-strength border bg-white": !isSelected },
      )}>
      <div
        className={cn("flex flex-col items-stretch", {
          "md:flex-row": imageSrc,
        })}>
        {imageSrc && (
          <div className="relative h-60 w-full shrink-0 overflow-hidden sm:h-44 md:aspect-auto md:h-auto md:min-h-50 md:w-60 md:self-stretch">
            <Image
              src={imageSrc}
              alt={imageAlt ?? ""}
              fill
              sizes="(max-width: 767px) 100vw, 240px"
              className="object-cover object-center"
            />
          </div>
        )}

        <div
          className={cn("flex min-w-0 shrink-0 flex-col gap-2 p-4 sm:p-6", {
            "md:min-h-0 md:flex-1": imageSrc,
          })}>
          {badges?.length ? (
            <div className="flex flex-wrap gap-2">
              {badges?.map((badge) => (
                <div
                  key={badge?.label}
                  className="bg-secondary inline-flex w-fit rounded-full px-3 py-1">
                  <Text
                    variant="body4"
                    component="span"
                    className="font-normal text-white">
                    {badge?.label}
                  </Text>
                </div>
              ))}
            </div>
          ) : null}
          <Text variant="h5" component="h3" className="text-greyscale-headline">
            {title}
          </Text>
          <RichText>{description}</RichText>
          <div
            className={cn(
              "mt-2 flex min-w-0 flex-col flex-nowrap gap-3 md:flex-row md:items-end",
              { "justify-between": monitoringLegal?.length },
              { "justify-end": !monitoringLegal?.length },
            )}>
            {monitoringLegal?.length ? (
              <div className="text-greyscale-body mr-3 max-w-[min(100%,28rem)] min-w-0 flex-1 shrink text-left text-sm leading-relaxed">
                <RichText>{monitoringLegal}</RichText>
              </div>
            ) : null}
            <div className="flex flex-col items-end">
              {promotions?.callout ? (
                <PromoBadge
                  label={promotions?.callout}
                  testId="monitoring-plan-card-promo-badge"
                  className="items-center"
                  iconSize={16}
                />
              ) : null}
              <Price
                value={price}
                monthly
                className={cn("text-greyscale-headline text-xl font-bold", {
                  "ml-auto text-right": !monitoringLegal?.length,
                })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export type MonitoringPlanCardProps = {
  badges?: Chip[];
  promotions?: NonNullable<
    RecommendedMonitoringPlansQueryResult[number]
  >["promotions"];
  monitoringLegal?: RichTextType;
  title: string;
  description?: RichTextType | null | undefined;
  price: number;
  badge?: string;
  imageSrc?: string;
  imageAlt?: string;
  isSelected?: boolean;
  onClick?: () => void;
};
